export type RuntimeMode = "webxr" | "camera-viewer" | "non-ar";

export type RuntimePhase =
  | "idle"
  | "checking"
  | "loading-engine"
  | "requesting-permission"
  | "searching-surface"
  | "placed"
  | "paused"
  | "recovering"
  | "error"
  | "stopped";

export type RuntimeStopReason = "user" | "session-ended" | "error" | "unmount";

export interface PlacementPose {
  position: [number, number, number];
  quaternion: [number, number, number, number];
  scale: number;
}

export type PlacementMode = "surface" | "fixed";

export interface RuntimeSupport {
  supported: boolean;
  reason?: string;
}

export interface CapabilityReport {
  secureContext: boolean;
  webxr: boolean;
  camera: boolean;
  coarsePointer: boolean;
  webgl: boolean;
  preferredMode: RuntimeMode;
}

export interface RuntimeSnapshot {
  mode: RuntimeMode;
  phase: RuntimePhase;
  placement: PlacementPose | null;
  error: string | null;
  placementMode?: PlacementMode;
}

export interface RuntimeAdapter {
  readonly mode: RuntimeMode;
  probe(): Promise<RuntimeSupport>;
  start(): Promise<void>;
  stop(reason: RuntimeStopReason): Promise<void>;
  reposition(): Promise<void>;
  acceptPlacement(pose: PlacementPose): void;
  getSnapshot(): RuntimeSnapshot;
  subscribe(listener: (snapshot: RuntimeSnapshot) => void): () => void;
}

/** Camera-relative pose for the non-AR and camera-viewer modes. */
export const DEFAULT_PLACEMENT: PlacementPose = {
  position: [0, -0.55, -3.4],
  quaternion: [0, 0, 0, 1],
  scale: 0.72,
};

// WebXR uses a local-floor reference space. Keep this separate from the
// desktop viewer pose, whose negative Y value is intentionally camera-relative.
export const WEBXR_FIXED_PLACEMENT: PlacementPose = {
  position: [0, 0, -1.5],
  quaternion: [0, 0, 0, 1],
  scale: 0.55,
};
